// What a MyGaz admin can do on an order from its drawer: hand it to a rider
// (or change rider, or name a first install's technician) and call it off
// with a reason. The agency's rules apply on the server (0045 goes through
// C18's dispatch_assign and dispatch_cancel); every action is journaled.
import { useEffect, useState } from 'react';
import { S } from '../lib/strings';
import type { Source } from '../lib/api';
import type { CancelReason, OpsOrder, PickRider } from '../lib/types';
import { discColor, initials } from '../lib/format';
import { useToast } from './Toast';
import { Ic } from './Icon';

export type ActMode = 'assign' | 'cancel' | null;

export const canAssign = (o: OpsOrder) =>
  o.order_type === 'first_install' ? o.status === 'placed' : o.status === 'placed' || o.status === 'assigned';
export const canCancel = (o: OpsOrder) =>
  o.order_type === 'first_install' ? o.status === 'placed' || o.status === 'assigned' : ['placed', 'assigned', 'agent_en_route'].includes(o.status);
export const assignLabel = (o: OpsOrder) => (o.order_type === 'first_install' ? S.act.name : o.rider ? S.act.reassign : S.act.assign);

export const errText = (e: unknown) => {
  const m = String((e as { message?: string })?.message ?? e);
  return S.act.err[m] ?? S.act.err.generic;
};

export function OrderActions({ o, mode, setMode, src, onDone }: { o: OpsOrder; mode: ActMode; setMode(m: ActMode): void; src: Source; onDone(): void }) {
  const toast = useToast();
  const [riders, setRiders] = useState<PickRider[] | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [why, setWhy] = useState<CancelReason | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null); setPick(null); setWhy(null); setNote('');
    if (mode === 'assign') { setRiders(null); src.ridersFor(o.id).then(setRiders).catch((e) => setErr(errText(e))); }
  }, [mode, o.id, src]);

  if (!mode) return null;
  const into = (el: HTMLDivElement | null) => { if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 30); };
  const run = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true); setErr(null);
    try { await f(); toast(ok); setMode(null); onDone(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  if (mode === 'assign') {
    const chosen = riders?.find((r) => r.id === pick);
    return (
      <div className="act" ref={into}>
        <div className="act-h"><b>{S.act.pick}</b><span>{S.act.pickSub}</span></div>
        {!riders ? <div className="loading-dot" style={{ margin: '24px auto' }} /> : riders.length === 0 ? <div className="muted">{S.act.noRider}</div> : (
          <div className="act-list" role="radiogroup" aria-label={S.act.pick}>
            {riders.map((r) => {
              const off = !r.live || !!r.busy;
              return (
                <button key={r.id} role="radio" aria-checked={pick === r.id} className={'act-r' + (pick === r.id ? ' on' : '') + (off ? ' dim' : '')} onClick={() => setPick(r.id)}>
                  <span className="ini" style={{ background: discColor(r.id) }}>{initials(r.name)}</span>
                  <span className="act-n"><b>{r.name}</b><span>{[r.plate, r.rating ? '★ ' + r.rating : null, r.can_install ? S.act.installer : null].filter(Boolean).join(' · ')}</span></span>
                  <span className={'st ' + (r.busy ? 'blue' : r.live ? 'green' : 'grey')}>{r.busy ? S.act.busy : r.live ? S.act.free : S.act.offline}</span>
                  <span className="act-dot">{pick === r.id && <Ic n="check" s="xs" />}</span>
                </button>
              );
            })}
          </div>
        )}
        {err && <p className="login-err" role="alert">{err}</p>}
        <div className="act-f">
          <button className="btn sm" onClick={() => setMode(null)}>{S.act.back}</button>
          <span className="sp" />
          <button className="btn sm pri" disabled={!chosen || busy} onClick={() => chosen && run(() => src.assign(o.id, chosen.id, o.order_type === 'first_install'), S.act.assigned(chosen.name))}>
            {chosen ? S.act.confirmAssign(chosen.name.split(' ')[0]) : S.act.pick}
          </button>
        </div>
        <div className="act-j"><Ic n="doc" s="xs" />{S.act.journal}</div>
      </div>
    );
  }

  return (
    <div className="act" ref={into}>
      <div className="act-h"><b>{S.act.why}</b><span>{S.act.whySub}</span></div>
      <div className="act-chips">
        {(Object.keys(S.act.reasons) as CancelReason[]).map((k) => (
          <button key={k} className="fchip" aria-pressed={why === k} onClick={() => setWhy(k)}>{S.act.reasons[k]}</button>
        ))}
      </div>
      <label className="fld" style={{ marginTop: 12 }}><span>{S.act.note}</span>
        <input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} /></label>
      {err && <p className="login-err" role="alert">{err}</p>}
      <div className="act-f">
        <button className="btn sm" onClick={() => setMode(null)}>{S.act.back}</button>
        <span className="sp" />
        <button className="btn sm danger" disabled={!why || busy} onClick={() => why && run(() => src.cancel(o.id, why, note.trim() || undefined), S.act.cancelled)}>{S.act.confirmCancel}</button>
      </div>
      <div className="act-j"><Ic n="doc" s="xs" />{S.act.journal}</div>
    </div>
  );
}

/** The "gas running low" reminder by hand, from the Overview alert or a home. */
export function useRemind(src: Source, onDone?: () => void) {
  const toast = useToast();
  return async (ids: string[]) => {
    try {
      const r = await src.remind(ids);
      toast(S.act.reminded(r.sent + r.skipped) + (r.skipped ? S.act.remindSkipped(r.skipped) : ''));
      onDone?.();
    } catch (e) { toast(errText(e)); }
  };
}
